import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Users, ShieldCheck, BookOpen, TrendingUp, Trash2, CheckCircle2, XCircle, Clock, Mail } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [extra, setExtra] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [pending, setPending] = useState([]);
  const [logins, setLogins] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const [s, e, u, c, p, l, ct] = await Promise.all([
      api.get("/analytics/admin"),
      api.get("/admin/dashboard-extra"),
      api.get("/admin/users"),
      api.get("/courses"),
      api.get("/admin/staff/pending"),
      api.get("/admin/logins?limit=200"),
      api.get("/admin/contact"),
    ]);
    setStats(s.data); setExtra(e.data); setUsers(u.data); setCourses(c.data);
    setPending(p.data); setLogins(l.data); setContacts(ct.data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/staff/${id}/approve`); toast.success("Staff approved"); load(); }
    catch (err) { toast.error("Failed"); }
  };
  const reject = async (id) => {
    if (!confirm("Reject this staff registration?")) return;
    try { await api.post(`/admin/staff/${id}/reject`); toast.success("Staff rejected"); load(); }
    catch (err) { toast.error("Failed"); }
  };
  const delUser = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const delCourse = async (id) => {
    if (!confirm("Delete this course?")) return;
    await api.delete(`/courses/${id}`); toast.success("Course deleted"); load();
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "approvals", label: `Pending (${pending.length})` },
    { id: "students", label: "Students" },
    { id: "staff", label: "Staff" },
    { id: "courses", label: "Courses" },
    { id: "logins", label: "Login History" },
    { id: "contact", label: "Contact Msgs" },
  ];

  return (
    <Layout title="Admin Console" subtitle="System · Overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Kpi label="Users" value={stats?.users_total ?? "—"} icon={Users} color="blue" testid="kpi-users" />
        <Kpi label="Students" value={stats?.students ?? "—"} icon={Users} color="emerald" testid="kpi-students" />
        <Kpi label="Staff" value={stats?.staff ?? "—"} icon={ShieldCheck} color="violet" testid="kpi-staff" />
        <Kpi label="Courses" value={stats?.courses ?? "—"} icon={BookOpen} color="amber" testid="kpi-courses" />
        <Kpi label="Pending" value={extra?.pending_staff ?? "—"} icon={Clock} color="pink" testid="kpi-pending" />
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="admin-tabs">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${tab === t.id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-blue-400"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-blue-600" /><h2 className="font-display font-bold text-xl text-slate-900">Signups</h2></div>
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={stats?.signup_series || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="signups" stroke="#2563EB" strokeWidth={2.5} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg text-slate-900 mb-2">System metrics</h3>
            <ul className="text-sm space-y-2">
              <li className="flex justify-between"><span className="text-slate-500">Enrollments</span><span className="font-mono">{stats?.enrollments}</span></li>
              <li className="flex justify-between"><span className="text-slate-500">Submissions</span><span className="font-mono">{stats?.submissions}</span></li>
              <li className="flex justify-between"><span className="text-slate-500">Quiz attempts</span><span className="font-mono">{stats?.quiz_attempts}</span></li>
              <li className="flex justify-between"><span className="text-slate-500">Logins today</span><span className="font-mono">{extra?.logins_today}</span></li>
              <li className="flex justify-between"><span className="text-slate-500">Contact msgs</span><span className="font-mono">{extra?.contact_messages}</span></li>
            </ul>
          </div>
        </div>
      )}

      {tab === "approvals" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="approvals-panel">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Pending staff approvals</h3>
          {pending.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">No pending requests</div>
          ) : (
            <div className="space-y-3">
              {pending.map((s) => (
                <div key={s.id} data-testid={`pending-${s.id}`} className="border border-amber-200 bg-amber-50/40 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900">{s.full_name || s.name} <span className="text-xs text-slate-500">· {s.email}</span></div>
                    <div className="text-xs text-slate-500 mt-1">{s.department} · {s.college_name} · #{s.register_number} · {s.mobile}</div>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`approve-${s.id}`} onClick={() => approve(s.id)} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Approve</button>
                    <button data-testid={`reject-${s.id}`} onClick={() => reject(s.id)} className="px-4 py-2 rounded-full border border-red-300 text-red-600 text-sm hover:bg-red-50 inline-flex items-center gap-1"><XCircle className="w-4 h-4" /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(tab === "students" || tab === "staff") && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4 capitalize">{tab}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`table-${tab}`}>
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-100">
                  <th className="py-2">Name</th><th>Email</th><th>Reg #</th><th>Dept</th><th>College</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {users.filter((u) => u.role === (tab === "students" ? "student" : "staff")).map((u) => (
                  <tr key={u.id} className="border-b border-slate-50">
                    <td className="py-2">{u.full_name || u.name}</td>
                    <td className="text-slate-600">{u.email}</td>
                    <td className="text-slate-500 font-mono text-xs">{u.register_number || "-"}</td>
                    <td className="text-slate-500 text-xs">{u.department || "-"}</td>
                    <td className="text-slate-500 text-xs">{u.college_name || "-"}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.status === "approved" || u.status === "active" ? "bg-emerald-50 text-emerald-700" : u.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                        {u.status || "active"}
                      </span>
                    </td>
                    <td>
                      <button data-testid={`del-user-${u.id}`} onClick={() => delUser(u.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {users.filter((u) => u.role === (tab === "students" ? "student" : "staff")).length === 0 && (
                  <tr><td colSpan={7} className="py-6 text-center text-slate-500 text-sm">None yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "courses" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Courses</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {courses.map((c) => (
              <div key={c.id} className="border border-slate-100 rounded-xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold line-clamp-1">{c.title}</div>
                  <div className="text-xs text-slate-500">{c.trainer_name} · {c.enrolled_count} enrolled</div>
                </div>
                <button data-testid={`admin-del-course-${c.id}`} onClick={() => delCourse(c.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "logins" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Login history</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="login-history-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-100">
                  <th className="py-2">When</th><th>Name</th><th>Email</th><th>Role</th><th>Reg #</th><th>IP</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {logins.map((l) => (
                  <tr key={l.id} className="border-b border-slate-50">
                    <td className="py-2 text-slate-500 text-xs">{new Date(l.timestamp).toLocaleString()}</td>
                    <td>{l.name}</td>
                    <td className="text-slate-500">{l.email}</td>
                    <td><span className="text-xs capitalize px-2 py-0.5 rounded-full bg-slate-100">{l.role}</span></td>
                    <td className="text-slate-500 font-mono text-xs">{l.register_number || "-"}</td>
                    <td className="text-slate-500 font-mono text-xs">{l.ip || "-"}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                        {l.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {logins.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500 text-sm">No login events yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "contact" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="contact-messages-panel">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4 flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" /> Contact messages</h3>
          {contacts.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">No messages yet.</div> : (
            <div className="space-y-3">
              {contacts.map((m) => (
                <div key={m.id} className="border border-slate-100 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <div className="font-semibold text-slate-900">{m.subject}</div>
                    <div className="text-xs text-slate-500">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-slate-500">{m.name} · {m.email}</div>
                  <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

function Kpi({ label, value, icon: Icon, color, testid }) {
  const map = { blue: "bg-blue-50 text-blue-700", emerald: "bg-emerald-50 text-emerald-700", violet: "bg-violet-50 text-violet-700", amber: "bg-amber-50 text-amber-700", pink: "bg-pink-50 text-pink-700" };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className={`w-9 h-9 rounded-xl ${map[color]} flex items-center justify-center`}><Icon className="w-4 h-4" /></div>
      <div className="mt-2 font-display font-black text-2xl text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
